import type {
  DataSource,
  Destination,
  DestinationDetail,
  DestinationKind,
  DestinationLifecycle,
  EntityScope,
  SourceCategory,
  AdapterKind,
} from '../../types';

// Pure display logic for the Connections tab (SC-3122), kept out of the components
// so the rules the design note settled are tested on their own.

export type Direction = 'inbound' | 'outbound';

// `unavailable` is inbound-only: switched on, but this build has no adapter for it,
// so nothing is read. Showing it as "On" would claim data flows that does not.
export type ConnectionStatus = 'on' | 'paused' | 'removing' | 'unavailable';

/**
 * One row of the registry — one connection INSTANCE in one direction (design note
 * 2026-09-08). A PIM that both pulls and pushes is two rows with the same
 * `connectionKey` and independent statuses.
 */
export interface ConnectionRow {
  id: string;
  direction: Direction;
  name: string;
  /** The integration — `provider` outbound, `adapter_key` inbound. */
  connectionKey: string;
  role: Described;
  status: ConnectionStatus;
}

/** A display label, plus an explanation when we have one. */
export interface Described {
  label: string;
  hint?: string;
}

/** The detail route for a row. */
export function connectionHref(direction: Direction, id: string): string {
  return `/connections/${direction}/${encodeURIComponent(id)}`;
}

/** Outbound status. `deleting` wins over `enabled` — it is one-way. */
export function destinationStatus(
  d: Pick<Destination, 'enabled'> & { lifecycle?: DestinationLifecycle },
): ConnectionStatus {
  if (d.lifecycle === 'deleting') return 'removing';
  return d.enabled ? 'on' : 'paused';
}

/** Inbound status. A source whose adapter this build does not have reads nothing,
 *  whatever its `enabled` flag says — so it is never shown as "On". */
export function sourceStatus(s: Pick<DataSource, 'enabled' | 'adapter_kind'>): ConnectionStatus {
  if (!s.enabled) return 'paused';
  return s.adapter_kind === null ? 'unavailable' : 'on';
}

/**
 * Flatten both directions into one sorted list: by connection key, then
 * direction (inbound first), then name — so every instance of one integration
 * sits together, in both directions, whatever order the API returns.
 *
 * `details` (admins only) supplies `lifecycle` and the post-toggle `enabled`; a
 * row without one falls back to the ungated list.
 */
export function buildConnectionRows(
  destinations: Destination[],
  details: Map<string, DestinationDetail>,
  sources: DataSource[],
): ConnectionRow[] {
  const rows: ConnectionRow[] = [
    ...sources.map((s) => ({
      id: s.id,
      direction: 'inbound' as const,
      name: s.name,
      connectionKey: s.adapter_key,
      role: categoryLabel(s.category),
      status: sourceStatus(s),
    })),
    ...destinations.map((d) => ({
      id: d.id,
      direction: 'outbound' as const,
      name: d.name,
      connectionKey: d.provider,
      role: kindLabel(d.kind),
      status: destinationStatus(details.get(d.id) ?? d),
    })),
  ];
  return rows.sort(
    (a, b) =>
      connectionLabel(a.connectionKey).localeCompare(connectionLabel(b.connectionKey)) ||
      a.connectionKey.localeCompare(b.connectionKey) ||
      (a.direction === b.direction ? 0 : a.direction === 'inbound' ? -1 : 1) ||
      a.name.localeCompare(b.name),
  );
}

/**
 * A connection key as words — the table's sort key: `web_search` → "Web search",
 * and a namespaced plugin key `ekom/apollo-org` → "Apollo org" (the namespace is
 * who ships the adapter, not what it is), so it sorts under A rather than E.
 * Generic on purpose — the UI never special-cases a system by name.
 */
export function connectionLabel(key: string): string {
  return humanize(key.slice(key.lastIndexOf('/') + 1));
}

/** `snake_case` / `kebab-case` → "Sentence case"; returns the input if nothing is left. */
export function humanize(value: string): string {
  const words = value.replace(/[_-]+/g, ' ').trim();
  if (!words) return value;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// ⚠️ Every enum below arrives off the wire, and the backend owns it —
// `SourceCategory`'s own docstring calls itself "extensible: a new category is a
// one-line addition". A bare `RECORD[value]` returns `undefined` for a value this
// build has never seen, and the next `.label` throws and takes the page down. So
// nothing indexes these maps directly: each has a lookup that falls back to the
// humanized wire value, which is honest (it is what the backend called it) and
// never crashes.

export function kindLabel(kind: string): Described {
  return KIND_LABEL[kind as DestinationKind] ?? { label: humanize(kind) };
}

export function categoryLabel(category: string): Described {
  return CATEGORY_LABEL[category as SourceCategory] ?? { label: humanize(category) };
}

export function entityScopeLabel(scope: string): string {
  return ENTITY_SCOPE_LABEL[scope as EntityScope] ?? humanize(scope);
}

export function adapterKindLabel(kind: string): string {
  return ADAPTER_KIND_LABEL[kind as AdapterKind] ?? humanize(kind);
}

/** `kind` — who wins when the far side disagrees with us. */
const KIND_LABEL: Record<DestinationKind, Required<Described>> = {
  system_of_record: {
    label: 'System of record',
    hint: 'Its edits win — a change made there is never overwritten from EKOM.',
  },
  channel: {
    label: 'Channel',
    hint: 'EKOM’s values win — a change made there is not taken back into your catalog.',
  },
};

/** `category` — how much we believe what an inbound source says. */
const CATEGORY_LABEL: Record<SourceCategory, Required<Described>> = {
  client_stack: {
    label: 'Your systems',
    hint: 'Your own systems — PIM, ERP, commerce. Trusted most by default.',
  },
  authoritative: {
    label: 'Authoritative',
    hint: 'An external authority — a standards body, regulator or official API.',
  },
  inferred: {
    label: 'Inferred',
    hint: 'Derived signals — web search, extraction, reviews. Trusted least by default.',
  },
};

const ENTITY_SCOPE_LABEL: Record<EntityScope, string> = {
  all_entities: 'Every product',
  members_only: 'Assigned products only',
};

export type CoverageTone = 'all' | 'none' | 'some';

/** The org's catalog names by id — or why they are not available yet. */
export type CatalogNames = Map<string, string> | 'loading' | 'error';

export interface Coverage {
  tone: CoverageTone;
  label: string;
  /** Longer explanation, for a tooltip / secondary line. */
  hint?: string;
}

/**
 * Which catalogs a connection covers.
 *
 * ⚠️ `null` and `[]` are OPPOSITES and must never render alike: `null` is every
 * catalog (including ones created later), `[]` is none at all. A catalog id with
 * no match in the org's list (deleted since) is named as such rather than dropped,
 * so the count on screen is the count stored.
 */
export function describeCoverage(
  ids: string[] | null,
  catalogNames: CatalogNames,
): Coverage {
  if (ids === null) {
    return {
      tone: 'all',
      label: 'All catalogs',
      hint: 'Every catalog in your organization, including ones added later.',
    };
  }
  if (ids.length === 0) {
    return {
      tone: 'none',
      label: 'No catalogs',
      hint: 'No catalog is in scope, so nothing reaches it.',
    };
  }
  const count = `${ids.length} ${ids.length === 1 ? 'catalog' : 'catalogs'}`;
  // Names not resolved yet (or not at all): say how many, and why they are not
  // named — never "Unknown catalog", which claims the catalog no longer exists.
  if (catalogNames === 'loading') return { tone: 'some', label: count, hint: 'Loading catalog names…' };
  if (catalogNames === 'error') {
    return { tone: 'some', label: count, hint: 'Catalog names couldn’t be loaded.' };
  }
  const names = ids.map((id) => catalogNames.get(id) ?? 'Unknown catalog');
  return {
    tone: 'some',
    label: names.join(', '),
    hint: count,
  };
}

/**
 * Which fields may cross to a destination, from its boundary lists. `allowed_fields`
 * `null` = every field; a list = only those. `denied_fields` always subtracts.
 * Read-only in tier 1 — editing these is tier 2.
 */
export function describeFields(allowed: string[] | null, denied: string[]): string {
  if (allowed !== null) {
    const kept = allowed.filter((f) => !denied.includes(f));
    if (kept.length === 0) return 'No fields';
    return `Only ${kept.join(', ')}`;
  }
  if (denied.length === 0) return 'All fields';
  return `All fields except ${denied.join(', ')}`;
}

const ADAPTER_KIND_LABEL: Record<AdapterKind, string> = {
  connector: 'API connector',
  scraper: 'Web scraper',
  builtin: 'Built into EKOM',
};

/** Where the detail view's back link returns to — the table passes its own URL
 *  (with the direction filter) as router state. */
export interface ConnectionsBackState {
  from?: string;
}

/**
 * The back link's target. Router state is only ever set by our own table, but it
 * lands in a `<Link to>` — and react-router v7 renders an ABSOLUTE `to` as an
 * external link — so accept nothing but the connections table's own path.
 */
export function backTarget(state: unknown): string {
  const from = (state as ConnectionsBackState | null)?.from;
  return typeof from === 'string' && /^\/connections(\?|$)/.test(from) ? from : '/connections';
}

/**
 * Whether a destination may be switched on/off from the UI. Only an `active` one:
 * `deleting` is one-way, and a lifecycle this build does not know is treated the
 * same way — a kill switch is never offered on a state we cannot reason about.
 */
export function canToggle(d: { lifecycle: string }): boolean {
  return d.lifecycle === 'active';
}
